'use client';

import { useState, useCallback, useMemo, useRef } from 'react';
import Image from 'next/image';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import BgOrbs from './BgOrbs';
import { tryCtaScrollToFinal } from '../lib/tryNavigation';

/**
 * SCENE 9: AI-подсказка. Одноосевая композиция, не split-layout.
 * Фокус — маскот в центре. Кнопка "Чем заняться?" висит на уровне его рук
 * (визуально "вырастает" из галактики). AI-подсказка появляется ниже
 * с искрами, как буквальный результат волшебства.
 */

const SUGGESTIONS = [
  'Маша три месяца назад писала про «Форест Гамп». Сегодня вечер — самое время.',
  'Из канала «Книги до 30» у тебя сохранён «1984» Оруэлла. 327 страниц — как раз на пару поездок.',
  'У тебя в избранном кофейня «Прокуратор» на Покровке. До работы — десять минут пешком.',
  'Антон звал в Hollow Knight. Steam, 200₽, не пожалеешь.',
  'Мама прислала рецепт пирога с яблоками. Воскресенье — отличный повод.',
  'Severance остался недосмотренным с зимы. Вторая серия второго сезона ждёт.',
  'Плейлист «весна 2026» из Spotify — 24 трека на утро.',
  'Выставка в Гараже до 30 марта. Лучше сходить сейчас, чем жалеть в апреле.',
];

export default function Scene09Magic() {
  const reduced = useReducedMotion() ?? false;

  const goToVoting = useCallback(() => {
    document.getElementById('scene-voting')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const goToFinal = useCallback(() => {
    tryCtaScrollToFinal();
  }, []);

  return (
    <section className="relative min-h-screen w-full overflow-hidden bg-[#f3fcf0]">
      {/* Magical atmospheric haze — расширяет purple/pink дымку маскота
          на всю секцию: розовая аура центром, тёплый закатный отблеск,
          фиолетовая глубина у нижнего края. */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background: [
            'radial-gradient(ellipse 70% 60% at 50% 50%, rgba(232, 180, 220, 0.55) 0%, transparent 65%)',
            'radial-gradient(ellipse 50% 35% at 50% 55%, rgba(255, 200, 170, 0.45) 0%, transparent 55%)',
            'radial-gradient(ellipse 90% 45% at 50% 100%, rgba(160, 130, 210, 0.35) 0%, transparent 60%)',
            'radial-gradient(ellipse 80% 30% at 50% 0%, rgba(200, 170, 230, 0.25) 0%, transparent 70%)',
          ].join(', '),
        }}
      />
      <BgOrbs variant="warm" />
      {!reduced && <SparkleField />}

      <div className="relative max-w-5xl mx-auto w-full px-5 sm:px-8 py-20 lg:py-24 flex flex-col items-center">
        {/* Заголовок и сабтекст */}
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="text-center max-w-2xl mb-8 sm:mb-10"
        >
          <h2
            className="font-display font-bold text-[#161d16] leading-[0.96] tracking-[-0.02em] mb-5"
            style={{ fontSize: 'clamp(2.25rem, 6vw, 4rem)' }}
          >
            Не знаешь —{' '}
            <span className="text-[#b7102a] italic" style={{ fontStyle: 'italic' }}>
              спроси.
            </span>
          </h2>
          <p className="font-body text-[#4a5a49] text-base sm:text-lg leading-relaxed mx-auto max-w-xl">
            Одна кнопка — и бот вытаскивает идеи из твоего же каталога. Занятие на вечер, фильм на сегодня, книга в дорогу.
          </p>
        </motion.div>

        {/* Композиция: маскот + кнопка в его руках */}
        <MascotWithButton />

        {/* Continue CTA */}
        <div className="flex flex-col items-center gap-5 mt-10">
          <button
            type="button"
            onClick={goToFinal}
            className="group relative inline-flex items-center gap-2.5 px-7 py-4 rounded-2xl bg-[#b7102a] hover:bg-[#991020] active:scale-[0.98] text-white font-semibold text-base transition-all cursor-pointer font-body"
            style={{ boxShadow: '0 12px 32px -8px rgba(183,16,42,0.45)' }}
          >
            <span>Попробовать сейчас</span>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="transition-transform group-hover:translate-x-0.5">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </button>
          <button
            type="button"
            onClick={goToVoting}
            className="group inline-flex items-center gap-2 text-[#4a5a49] hover:text-[#161d16] font-body text-sm font-medium transition-colors"
          >
            <span>А можно выбрать с кем-то?</span>
            <motion.svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              animate={reduced ? {} : { y: [0, 4, 0] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
            >
              <path d="M12 5v14M19 12l-7 7-7-7" />
            </motion.svg>
          </button>
        </div>
      </div>
    </section>
  );
}

/* ────────────── MascotWithButton ────────────── */

function MascotWithButton() {
  const reduced = useReducedMotion() ?? false;
  const [shown, setShown] = useState<string | null>(null);
  const [counter, setCounter] = useState(0);
  const popupRef = useRef<HTMLDivElement>(null);

  const handleClick = () => {
    const pool = SUGGESTIONS.filter((s) => s !== shown);
    const next = pool[Math.floor(Math.random() * pool.length)];
    setShown(next);
    setCounter((c) => c + 1);
    // Scroll popup в viewport — особенно нужно на мобиле, где он улетает под фолд.
    // Ждём пока React смонтирует/перемонтирует popup (после exit-анимации старого).
    window.setTimeout(() => {
      popupRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 380);
  };

  return (
    <div className="relative w-full flex flex-col items-center">
      {/* Маскот: чистая rounded-карточка с soft shadow и подложкой-glow */}
      <motion.div
        initial={reduced ? false : { opacity: 0, scale: 0.94, y: 12 }}
        whileInView={{ opacity: 1, scale: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-[300px] sm:w-[380px] lg:w-[440px] aspect-square"
      >
        {/* Внешний warm glow вокруг карточки */}
        <div
          aria-hidden
          className="absolute -inset-6 rounded-[2.5rem]"
          style={{
            background:
              'radial-gradient(ellipse 60% 55% at 50% 60%, rgba(245,168,90,0.55) 0%, rgba(183,16,42,0.30) 35%, transparent 70%)',
            filter: 'blur(32px)',
          }}
        />
        {/* Сам маскот в rounded-контейнере с тенью */}
        <div
          className="relative w-full h-full rounded-[2rem] overflow-hidden border border-white/60"
          style={{
            boxShadow:
              '0 32px 80px -24px rgba(20,40,20,0.45), 0 0 0 1px rgba(255,255,255,0.6) inset',
          }}
        >
          <Image
            src="/landing/110526/magic-mascot.webp"
            alt="Маскот держит галактику в руках"
            fill
            sizes="(max-width: 640px) 300px, (max-width: 1024px) 380px, 440px"
            className="object-cover"
            priority={false}
          />
        </div>
      </motion.div>

      {/* Кнопка "Чем заняться?" — позиционирована поверх рук маскота */}
      <motion.div
        initial={reduced ? false : { opacity: 0, y: 8 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.6, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="relative -mt-12 sm:-mt-14 lg:-mt-16 z-10"
      >
        <div className="relative inline-block">
          {/* Двойная пульсация */}
          <motion.span
            aria-hidden
            className="absolute inset-0 rounded-full bg-[#b7102a] pointer-events-none"
            animate={reduced ? {} : { scale: [1, 1.4], opacity: [0.55, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
          />
          <motion.span
            aria-hidden
            className="absolute inset-0 rounded-full bg-[#f5a85a] pointer-events-none"
            animate={reduced ? {} : { scale: [1, 1.6], opacity: [0.4, 0] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: 'easeOut', delay: 0.6 }}
          />
          <button
            type="button"
            onClick={handleClick}
            className="relative inline-flex items-center gap-2 pl-5 pr-6 py-3.5 rounded-full bg-[#b7102a] hover:bg-[#991020] active:scale-[0.96] text-white font-semibold text-[15px] transition-colors font-body"
            style={{
              boxShadow:
                '0 16px 40px -8px rgba(183,16,42,0.6), 0 0 0 4px rgba(255,255,255,0.95) inset, 0 0 24px rgba(245,168,90,0.4)',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2l1.8 5.5L19 9l-4.5 3.3 1.7 5.7L12 14.8l-4.2 3.2 1.7-5.7L5 9l5.2-1.5L12 2z" />
            </svg>
            <span>Чем заняться?</span>
          </button>
        </div>
      </motion.div>

      {/* AI-подсказка — вырывается ниже кнопки, с искрами */}
      <AnimatePresence mode="wait">
        {shown && (
          <motion.div
            ref={popupRef}
            key={counter}
            initial={{ opacity: 0, y: 16, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, transition: { duration: 0.2 } }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="relative mt-6 max-w-md"
          >
            {/* Искры вокруг карточки */}
            {!reduced && <BurstSparkles seed={counter} />}
            <div
              className="relative bg-white rounded-2xl px-5 py-4 border border-[#dce5d9]"
              style={{ boxShadow: '0 24px 48px -16px rgba(20,40,20,0.3), 0 0 0 4px rgba(245,168,90,0.08)' }}
            >
              <div className="flex items-center gap-1.5 mb-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#b7102a] animate-pulse" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#b7102a]">
                  Подсказка
                </span>
              </div>
              <div className="font-body text-[#161d16] text-[14px] leading-snug">{shown}</div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ────────────── BurstSparkles — искры разлетаются от popup ────────────── */

function BurstSparkles({ seed }: { seed: number }) {
  const sparkles = useMemo(() => {
    const list: { x: number; y: number; size: number; delay: number; rot: number }[] = [];
    const rand = seedRand(seed * 9999 + 1);
    for (let i = 0; i < 8; i++) {
      list.push({
        x: (rand() - 0.5) * 240,
        y: -30 - rand() * 80,
        size: 4 + rand() * 6,
        delay: rand() * 0.2,
        rot: rand() * 90,
      });
    }
    return list;
  }, [seed]);

  return (
    <>
      {sparkles.map((s, i) => (
        <motion.svg
          key={i}
          width={s.size}
          height={s.size}
          viewBox="0 0 24 24"
          className="absolute left-1/2 top-1/2 pointer-events-none"
          style={{ color: i % 2 === 0 ? '#b7102a' : '#f5a85a' }}
          initial={{ x: 0, y: 0, opacity: 0, scale: 0, rotate: 0 }}
          animate={{ x: s.x, y: s.y, opacity: [0, 1, 0], scale: [0, 1.2, 0.8], rotate: s.rot }}
          transition={{ duration: 0.9, delay: s.delay, ease: 'easeOut' }}
        >
          <path d="M12 2l1.8 5.5L19 9l-4.5 3.3 1.7 5.7L12 14.8l-4.2 3.2 1.7-5.7L5 9l5.2-1.5L12 2z" fill="currentColor" />
        </motion.svg>
      ))}
    </>
  );
}

/* ────────────── SparkleField — фоновые частицы ────────────── */

function SparkleField() {
  const dots = useMemo(() => {
    const rand = seedRand(7);
    const list: { x: number; y: number; size: number; delay: number; duration: number; opacity: number }[] = [];
    for (let i = 0; i < 18; i++) {
      list.push({
        x: rand() * 100,
        y: rand() * 100,
        size: 2 + rand() * 4,
        delay: rand() * 4,
        duration: 4 + rand() * 4,
        opacity: 0.15 + rand() * 0.35,
      });
    }
    return list;
  }, []);

  return (
    <div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden">
      {dots.map((d, i) => (
        <motion.span
          key={i}
          className="absolute rounded-full bg-[#b7102a]"
          style={{
            left: `${d.x}%`,
            top: `${d.y}%`,
            width: d.size,
            height: d.size,
            opacity: d.opacity,
          }}
          animate={{ opacity: [d.opacity, d.opacity * 0.2, d.opacity], y: [0, -8, 0] }}
          transition={{ duration: d.duration, delay: d.delay, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}
    </div>
  );
}

/* ────────────── helpers ────────────── */

function seedRand(seed: number) {
  let s = seed | 0;
  return function () {
    s = (s + 0x6d2b79f5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
